import { useEffect, useState } from "react";

// Smooth scrolling hook
export function useSmoothScrolling() {
  useEffect(() => {
    const navLinks = document.querySelectorAll('a[href^="#"]');

    const handleClick = function (e) {
      const targetId = this.getAttribute("href");
      const targetElement = document.querySelector(targetId);

      if (targetElement) {
        e.preventDefault();

        // Calculate offset for header
        const headerHeight = document.querySelector(".header")?.offsetHeight || 0;
        const targetPosition = targetElement.offsetTop - headerHeight - 20;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }
    };

    navLinks.forEach((link) => {
      link.addEventListener("click", handleClick);
    });

    return () => {
      navLinks.forEach((link) => {
        link.removeEventListener("click", handleClick);
      });
    };
  }, []);
}

// Header scroll effect hook
export function useHeaderScrollEffect() {
  useEffect(() => {
    const header = document.querySelector(".header");
    if (!header) return;

    let lastScrollTop = 0;

    const handleScroll = function () {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Add shadow when scrolled
      if (scrollTop > 10) {
        header.classList.add("scrolled");
      } else {
        header.classList.remove("scrolled");
      }

      lastScrollTop = scrollTop;
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
}

// LogoStrip carousel hook - handles all logostrip sections
export function useLogoStripCarousel(deps = []) {
  useEffect(() => {
    // Find all sections with logostrip
    const sections = document.querySelectorAll(".logostrip-section");

    sections.forEach((section) => {
      const track = section.querySelector(".logostrip-track");
      if (!track) return;

      // Reset clones and previous listeners
      const existingClones = track.querySelectorAll(".logostrip-item[data-clone='true']");
      existingClones.forEach((clone) => clone.remove());
      track.onmouseenter = null;
      track.onmouseleave = null;

      const items = track.querySelectorAll(".logostrip-item:not([data-clone='true'])");
      if (!items.length) return;

      const containerWidth = section.querySelector(".logostrip-container")?.clientWidth || 0;
      const singleSetWidth = Array.from(items).reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);

      if (singleSetWidth <= 0) return;

      // Build a long enough first cycle so low item counts don't leave visual gaps.
      const cycleRepeatCount = Math.max(1, Math.ceil((containerWidth * 1.2) / singleSetWidth));

      for (let repeatIndex = 1; repeatIndex < cycleRepeatCount; repeatIndex += 1) {
        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute("data-clone", "true");
          track.appendChild(clone);
        });
      }

      // Duplicate the full first cycle once more for seamless infinite scroll.
      const firstCycleItems = track.querySelectorAll(".logostrip-item");
      firstCycleItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute("data-clone", "true");
        track.appendChild(clone);
      });

      const trackWidth = track.scrollWidth / 2;
      const pixelsPerSecond = 40;
      const duration = trackWidth / pixelsPerSecond;

      track.style.animationDuration = `${duration}s`;

      // Pause animation on hover
      const handleMouseEnter = () => {
        track.style.animationPlayState = "paused";
      };

      const handleMouseLeave = () => {
        track.style.animationPlayState = "running";
      };

      track.onmouseenter = handleMouseEnter;
      track.onmouseleave = handleMouseLeave;

      // Track interactions
      const allBoxes = track.querySelectorAll(".logostrip-box");
      allBoxes.forEach((box, index) => {
        const itemIndex = index % items.length;
        const logo = box.querySelector(".logostrip-logo");
        const itemName = logo ? logo.alt : `item_${itemIndex + 1}`;

        const handleClick = () => {
          console.log(`LogoStrip item ${itemName} clicked`);

          if (window.websiteTracker) {
            window.websiteTracker.trackInteraction("logostrip_click", {
              item: itemName,
              timestamp: Date.now(),
            });
          }
        };

        box.onclick = handleClick;
      });
    });
  }, deps);
}

// Announcement bar hook
export function useAnnouncementBar() {
  const [announcementMessages, setAnnouncementMessages] = useState([]);
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const [announcementLang, setAnnouncementLang] = useState(null);

  useEffect(() => {
    const initializeAnnouncementBar = (options = {}) => {
      const { keepIndex = false } = options;

      const textElement = document.querySelector(".announcement-text");
      const barElement = document.querySelector(".announcement-bar");
      const trackElement = document.querySelector(".announcement-track");

      if (!textElement || !barElement || !trackElement) return;

      const currentLang = window.languageManager ? window.languageManager.getCurrentLanguage() : "vi";

      const langData = window.translations && window.translations[currentLang];
      if (!langData || !langData.announcement) return;

      const a = langData.announcement;

      // default -> special1 -> special2 -> special3 ...
      const newMessages = [a.defaultMessage, a.specialMessage1, a.specialMessage2, a.specialMessage3, a.specialMessage4].filter(Boolean);

      if (!newMessages.length) return;

      // nếu ngôn ngữ thay đổi
      if (announcementLang !== currentLang) {
        setAnnouncementLang(currentLang);

        if (!keepIndex) {
          // luôn bắt đầu lại từ default
          setAnnouncementIndex(0);
        } else {
          // giữ nguyên index logic, nhưng clamp để không vượt quá độ dài mới
          if (announcementIndex >= newMessages.length) {
            setAnnouncementIndex(newMessages.length - 1);
          }
        }
      }

      setAnnouncementMessages(newMessages);

      // hiển thị message hiện tại tương ứng index logic
      textElement.textContent = newMessages[announcementIndex];
      barElement.style.display = "block";

      if (!trackElement.dataset.announcementBound) {
        const handleAnimationIteration = () => {
          setAnnouncementIndex((prev) => {
            const nextIndex = (prev + 1) % newMessages.length;
            textElement.textContent = newMessages[nextIndex];
            return nextIndex;
          });
        };

        trackElement.addEventListener("animationiteration", handleAnimationIteration);
        trackElement.dataset.announcementBound = "true";
      }
    };

    // Initialize on mount
    initializeAnnouncementBar();

    // Listen for language changes
    const handleLanguageChange = (e) => {
      const newLang = e.detail.language;
      initializeAnnouncementBar({ keepIndex: true });
    };

    document.addEventListener("languageChanged", handleLanguageChange);

    return () => {
      document.removeEventListener("languageChanged", handleLanguageChange);
    };
  }, [announcementIndex, announcementLang]);

  return { announcementMessages, announcementIndex };
}

// Contact form hook
export function useContactForm() {
  useEffect(() => {
    const contactForm = document.querySelector("#contact-form");

    if (!contactForm) return;

    const handleSubmit = function (e) {
      e.preventDefault();

      // Lấy data form
      const formData = new FormData(this);
      const data = Object.fromEntries(formData);

      // Thêm thông tin ngôn ngữ
      const currentLang = window.languageManager ? window.languageManager.getCurrentLanguage() : "vi";
      data.language = currentLang;

      console.log("Contact form submitted:", data);

      // Tracking submit
      if (window.websiteTracker) {
        window.websiteTracker.trackInteraction("form_submit", {
          form: "contact",
          language: data.language,
        });
      }

      // Nếu muốn gửi email bằng mailto (tuỳ chọn)
      const emailTo = "support@example.com"; // đổi thành email thật
      const subject = `Contact form - ${currentLang}`;
      const bodyLines = [];

      // Ghép toàn bộ field vào body
      Object.keys(data).forEach((key) => {
        bodyLines.push(`${key}: ${data[key]}`);
      });

      const mailtoLink = `mailto:${emailTo}` + `?subject=${encodeURIComponent(subject)}` + `&body=${encodeURIComponent(bodyLines.join("\n"))}`;

      // Mở trình gửi mail
      window.location.href = mailtoLink; // dùng mailto từ JS [web:19]

      // Thông báo thành công theo ngôn ngữ
      const successMessages = {
        vi: "Cảm ơn bạn đã gửi tin nhắn! Chúng tôi sẽ phản hồi sớm.",
        en: "Thank you for your message! We will get back to you soon.",
      };

      alert(successMessages[currentLang] || successMessages.vi);
    };

    contactForm.addEventListener("submit", handleSubmit);

    return () => {
      contactForm.removeEventListener("submit", handleSubmit);
    };
  }, []);
}

// Search functionality hook
export function useSearch() {
  useEffect(() => {
    const searchButton = document.querySelector(".search-btn");

    if (searchButton) {
      const handleClick = function () {
        const currentLang = window.languageManager ? window.languageManager.getCurrentLanguage() : "vi";

        const searchPrompts = {
          vi: "Bạn muốn tìm kiếm gì?",
          en: "What would you like to search for?",
        };

        const searchQuery = prompt(searchPrompts[currentLang] || searchPrompts["vi"]);

        if (searchQuery) {
          console.log(`Searching for: ${searchQuery} (Language: ${currentLang})`);

          // Track search
          if (window.websiteTracker) {
            window.websiteTracker.trackInteraction("search", {
              query: searchQuery,
              language: currentLang,
            });
          }
        }
      };

      searchButton.addEventListener("click", handleClick);

      return () => {
        searchButton.removeEventListener("click", handleClick);
      };
    }
  }, []);
}

// Footer link tracking hook
export function useFooterTracking() {
  useEffect(() => {
    const footerLinks = document.querySelectorAll(".footer-links a");

    const handleClick = function () {
      const linkText = this.textContent;
      const currentLang = window.languageManager ? window.languageManager.getCurrentLanguage() : "vi";

      console.log(`Footer link clicked: ${linkText} (Language: ${currentLang})`);

      // Track with analytics
      if (window.websiteTracker) {
        window.websiteTracker.trackInteraction("footer_link_click", {
          link: this.getAttribute("data-track"),
          text: linkText,
          language: currentLang,
        });
      }
    };

    footerLinks.forEach((link) => {
      link.addEventListener("click", handleClick);
    });

    return () => {
      footerLinks.forEach((link) => {
        link.removeEventListener("click", handleClick);
      });
    };
  }, []);
}

// Enhanced card interactions hook
export function useCardInteractions(deps = []) {
  useEffect(() => {
    const cards = document.querySelectorAll(".card");

    const handleClick = function () {
      const cardTitle = this.querySelector(".card-title")?.textContent;
      const currentLang = window.languageManager ? window.languageManager.getCurrentLanguage() : "vi";

      console.log(`Card clicked: ${cardTitle} (Language: ${currentLang})`);

      // Track with analytics
      if (window.websiteTracker) {
        window.websiteTracker.trackInteraction("card_click", {
          card: this.getAttribute("data-track"),
          title: cardTitle,
          language: currentLang,
        });
      }
    };

    const handleKeyDown = function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.click();
      }
    };

    cards.forEach((card) => {
      card.addEventListener("click", handleClick);
      card.addEventListener("keydown", handleKeyDown);
    });

    return () => {
      cards.forEach((card) => {
        card.removeEventListener("click", handleClick);
        card.removeEventListener("keydown", handleKeyDown);
      });
    };
  }, deps);
}

// Language features hook
export function useLanguageFeatures() {
  useEffect(() => {
    // Update text direction for languages like Arabic (if added later)
    const currentLang = window.languageManager ? window.languageManager.getCurrentLanguage() : "vi";

    // Add language-specific CSS classes
    document.body.classList.add(`lang-${currentLang}`);

    // Handle right-to-left languages (for future expansion)
    const rtlLanguages = ["ar", "he", "fa"];
    if (rtlLanguages.includes(currentLang)) {
      document.body.setAttribute("dir", "rtl");
    } else {
      document.body.setAttribute("dir", "ltr");
    }

    // Listen for language changes
    const handleLanguageChange = (e) => {
      const newLang = e.detail.language;
      showLanguageChangeNotification(newLang);

      // Update language-specific features
      const body = document.body;
      body.className = body.className.replace(/lang-\w+/g, "");
      body.classList.add(`lang-${newLang}`);

      if (rtlLanguages.includes(newLang)) {
        body.setAttribute("dir", "rtl");
      } else {
        body.setAttribute("dir", "ltr");
      }
    };

    document.addEventListener("languageChanged", handleLanguageChange);

    return () => {
      document.removeEventListener("languageChanged", handleLanguageChange);
    };
  }, []);
}

// Language change notification
function showLanguageChangeNotification(newLang) {
  const langNames = {
    vi: "Tiếng Việt",
    en: "English",
  };

  const currentLang = newLang || (window.languageManager ? window.languageManager.getCurrentLanguage() : "vi");

  const prefix =
    window.translations && window.translations[currentLang] && window.translations[currentLang].langNotificationPrefix
      ? window.translations[currentLang].langNotificationPrefix
      : "Language changed to";

  const notification = document.createElement("div");
  notification.className = "lang-notification";
  notification.textContent = `${prefix} ${langNames[newLang] || langNames["vi"]}`;

  document.body.appendChild(notification);

  setTimeout(() => {
    notification.style.opacity = "1";
    notification.style.transform = "translateY(0)";
  }, 100);

  setTimeout(() => {
    notification.style.opacity = "0";
    notification.style.transform = "translateY(-20px)";
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 300);
  }, 2000);
}

// Utility function for API calls with language support
export async function fetchDataWithLang(url, lang = null) {
  const currentLang = lang || (window.languageManager ? window.languageManager.getCurrentLanguage() : "vi");

  try {
    const response = await fetch(`${url}?lang=${currentLang}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching ", error);
    return null;
  }
}

// Active navigation hook
export function useActiveNav() {
  useEffect(() => {
    // Lấy phần cuối của path: ví dụ /contact.html -> contact.html
    const currentPath = window.location.pathname.split("/").pop() || "index.html";

    // Điều chỉnh selector cho đúng cấu trúc menu của bạn
    const navLinks = document.querySelectorAll(".nav-menu a, .header nav a");

    navLinks.forEach((link) => {
      const href = link.getAttribute("href");

      if (href === currentPath) {
        link.classList.add("active");
      } else {
        link.classList.remove("active");
      }
    });
  }, []);
}

// Mobile menu setup hook
export function useMobileMenu() {
  useEffect(() => {
    const navMenu = document.querySelector(".nav-menu");

    // Add mobile menu button if screen is small
    if (window.innerWidth <= 768) {
      console.log("Mobile view detected");
    }

    // Handle window resize
    const handleResize = function () {
      if (window.innerWidth <= 768) {
        // Mobile view - ensure language buttons are visible
        console.log("Switched to mobile view");
      } else {
        // Desktop view
        console.log("Switched to desktop view");
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
}
